import React, { useEffect, useState } from "react";

import { getAnalyticsWidget } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";
import { AnalyticWidgetProps } from "./types";
import { WidgetFieldSchema } from "../../../types/analytics";
import { WidgetCard } from "../../../components/analytics";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";

/* eslint-disable camelcase */
const DISPLAY_NAME = "Recommendations Reach";

type RecsUserServedCountRow = {
    users_served: number | null;
    users_served_percent_change: number | null;
    total_impressions: number | null;
    total_impressions_percent_change: number | null;
    avg_recs_per_user: number | null;
    avg_recs_per_user_percent_change: number | null;
};

type ApiRow = Record<string, unknown>;

const toNumber = (value: unknown): number | null => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        return null;
    }

    return value;
};

export const calculatePercentChange = (currentValue: number | null, previousValue: number | null): number | null => {
    if (currentValue === null || previousValue === null || previousValue === 0) {
        return null;
    }

    return ((currentValue - previousValue) / previousValue) * 100;
};

export const normalizeRecsUserServedCountRow = (response: unknown): RecsUserServedCountRow | null => {
    const result = (response as { result?: unknown })?.result;
    const rawRow = Array.isArray(result) ? result[0] : result;

    if (typeof rawRow !== "object" || rawRow === null) {
        return null;
    }

    const row = rawRow as ApiRow;

    return {
        users_served: toNumber(row.users_served),
        users_served_percent_change: toNumber(row.users_served_percent_change),
        total_impressions: toNumber(row.total_impressions),
        total_impressions_percent_change: toNumber(row.total_impressions_percent_change),
        avg_recs_per_user: toNumber(row.avg_recs_per_user),
        avg_recs_per_user_percent_change: toNumber(row.avg_recs_per_user_percent_change)
    };
};

export const buildRecsUserServedCountMetrics = (
    row: RecsUserServedCountRow,
    schema?: Record<string, WidgetFieldSchema>
): MetricCardItem[] => {
    return [
        {
            key: "users_served",
            label: "Users Served",
            value: row.users_served,
            changePercent: row.users_served_percent_change,
            valueFormat: "number",
            description: schema?.users_served?.description
        },
        {
            key: "total_impressions",
            label: "Total Impressions",
            value: row.total_impressions,
            changePercent: row.total_impressions_percent_change,
            valueFormat: "number",
            description: schema?.total_impressions?.description
        },
        {
            key: "avg_recs_per_user",
            label: "Avg Recs / User",
            value: row.avg_recs_per_user,
            changePercent: row.avg_recs_per_user_percent_change,
            valueFormat: "decimal",
            description: schema?.avg_recs_per_user?.description
        }
    ];
};

const RecsUserServedCountWidget: React.FC<AnalyticWidgetProps> = ({
    customerConfig,
    filters,
    theme,
    onMetaLoaded
}) => {
    const [row, setRow] = useState<RecsUserServedCountRow | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [widgetMeta, setWidgetMeta] = useState<any>(null);
    const [schema, setSchema] = useState<Record<string, WidgetFieldSchema> | undefined>(undefined);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true);
            setError(null);

            try {
                const response = await getAnalyticsWidget(
                    customerConfig,
                    "recs_user_served_count",
                    filters.startDate,
                    filters.endDate,
                    filters.attributionId,
                    filters.brandName,
                    filters.devicePlatform
                );

                setRow(normalizeRecsUserServedCountRow(response));

                const meta = (response as { widget?: any })?.widget;
                const responseSchema = (response as { schema?: Record<string, WidgetFieldSchema> })?.schema;
                if (meta) {
                    setWidgetMeta(meta);
                    setSchema(responseSchema);
                    if (onMetaLoaded) {
                        onMetaLoaded({
                            widgetKey: "recs_user_served_count",
                            displayName: DISPLAY_NAME,
                            description: meta.description,
                            schema: responseSchema
                        });
                    }
                }
            } catch (err) {
                Logger.error("Error fetching recs user served count data", err);
                setError("Failed to fetch recs user served count data");
            } finally {
                setIsLoading(false);
            }
        };

        void fetchData();
    }, [customerConfig, filters.attributionId, filters.brandName, filters.devicePlatform, filters.endDate, filters.startDate, onMetaLoaded]);

    const metrics = row ? buildRecsUserServedCountMetrics(row, schema) : [];

    return (
        <WidgetCard
            title={DISPLAY_NAME}
            description={widgetMeta?.description}
            isLoading={isLoading}
            error={error}
            isEmpty={!isLoading && !error && !row}
            theme={theme}
            emptyMessage="No recommendation user-served data available for the selected date range."
        >
            <MetricCards
                metrics={metrics}
                theme={theme}
                noChangeLabel="No change data"
            />
        </WidgetCard>
    );
};

export default RecsUserServedCountWidget;
/* eslint-enable camelcase */